import axios from 'axios';
import { useAuthorizeContext } from 'pages/Authorize/AuthorizeContext';
import { getBackendUrl } from './get-backend-url';
import { useFlag } from '@unleash/proxy-client-react';
import { FeatureFlag } from 'constants/feature-flags';

const BACKEND_ROUTE = getBackendUrl();

// Simple queue to ensure events are sent in order
let queue: Array<() => Promise<void>> = [];
let processing = false;

async function processQueue() {
  if (processing || queue.length === 0) return;

  processing = true;

  while (queue.length > 0) {
    const task = queue.shift();
    if (task) {
      try {
        await task();
      } catch (error) {
        // Error already logged in the task
      }
    }
  }

  processing = false;
}

type TrackConversionOpts = {
  step: string;
  subStep?: string;
  action: string;
  sessionKey: string;
  providerId: string | null;
  providerImplementationKind: string | null;
  clientId: string;
  currentBrowserSessionKey: string;
  actionOccurredAt: Date;
};

type TrackConversionErrorOpts = {
  step: string;
  currentBrowserSessionKey: string;
  error: {
    statusCode?: string;
    finchCode?: string;
  };
  errorOccurredAt: Date;
};

export const trackConversionStep = (opts: TrackConversionOpts) => {
  // Add to queue and process
  queue.push(async () => {
    try {
      await axios.post(`${BACKEND_ROUTE}/auth/conversion-step`, opts);
    } catch (error) {
      console.error('Track conversion step failed', error);
    }
  });

  processQueue();
};

/**
 * Track a step of the Connect flow for the conversion rate metrics.
 */
export const useTrackConversionStep = () => {
  const authorizeContext = useAuthorizeContext();

  if (!authorizeContext) {
    throw new Error(
      'useTrackConversion must be used within an AuthorizeContext',
    );
  }

  const {
    currentStep,
    sessionKey,
    payrollProvider,
    providerImplementation,
    clientId,
    currentBrowserSessionKey,
  } = authorizeContext;

  return ({ action, substep }: { action: string; substep?: string }) => {
    return trackConversionStep({
      step: currentStep,
      subStep: substep,
      action,
      sessionKey,
      providerId: payrollProvider?.id || null,
      providerImplementationKind: providerImplementation?.kind || null,
      clientId,
      currentBrowserSessionKey,
      actionOccurredAt: new Date(),
    });
  };
};

export const trackConversionError = (opts: TrackConversionErrorOpts) => {
  // Add to queue and process
  queue.push(async () => {
    try {
      await axios.post(`${BACKEND_ROUTE}/auth/conversion-step-error`, opts);
    } catch (error) {
      console.error('Track conversion error failed', error);
    }
  });

  processQueue();
};

/**
 * Track an error in the Connect flow for the conversion rate metrics.
 */
export const useTrackConversionError = () => {
  const authorizeContext = useAuthorizeContext();

  if (!authorizeContext) {
    throw new Error(
      'useTrackConversionError must be used within an AuthorizeContext',
    );
  }

  const { currentStep, currentBrowserSessionKey } = authorizeContext;

  return ({ error }: { error: { statusCode?: string; finchCode?: string } }) =>
    trackConversionError({
      step: currentStep,
      currentBrowserSessionKey,
      error: {
        statusCode: error.statusCode?.toString(),
        finchCode: error.finchCode,
      },
      errorOccurredAt: new Date(),
    });
};
